import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft } from 'lucide-react';

export function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      setError('Failed to send reset email. Please check your email address.');
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center py-10 bg-cacao-dark">
        <div className="w-full max-w-sm px-4 text-center">
          <h1 className="font-heading text-lg font-medium text-[#F5F5F5]">Check your email</h1>
          <p className="mt-1.5 text-sm text-cacao-muted">
            We sent a password reset link to <strong className="text-[#F5F5F5]">{email}</strong>
          </p>
          <Link
            to={ROUTES.LOGIN}
            className="mt-6 inline-flex items-center gap-2 text-xs font-medium text-cacao-muted hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center py-10 bg-cacao-dark">
      <div className="w-full max-w-sm px-4">
        <h1 className="text-center font-heading text-lg font-medium text-[#F5F5F5]">Reset your password</h1>
        <p className="mt-1 text-center text-xs text-cacao-text-tertiary">
          Enter your email and we will send you a reset link.
        </p>

        {error && (
          <div className="mt-3 rounded-md bg-red-500/20 p-2.5 text-xs text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-3">
          <div>
            <label htmlFor="email" className="block text-xs font-medium text-cacao-muted">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-cacao-gold px-3 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover disabled:opacity-50 transition-colors"
          >
            {loading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>

        <p className="mt-6 text-center">
          <Link
            to={ROUTES.LOGIN}
            className="inline-flex items-center gap-2 text-xs font-medium text-cacao-muted hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to login
          </Link>
        </p>
      </div>
    </div>
  );
}
